import forge from "node-forge";
import { ENDPOINTS, api } from "services";
import { ActionModalsStore } from "stores/ActionModalsStore";
import { UserStore } from "stores/UserStore";
import { deleteFromDB, getFromDB, saveToDB } from "./indexedDB";
import { handleFetchError } from "utils/errors";

const salt = window.injectedEnv?.WE_ENV_SALT || "456meTestSalt123";

// функция для удаления сертификатов
export const clearCertDB = async (userId: string | undefined, companyId: string | undefined) => {
  if (!userId || !companyId) return Promise.reject("cert_error");
  await deleteFromDB(`${userId}_${companyId}`);
};

// делаем Base64 из PEM
const cleanPEM = (pem: string, startWord: string, endWord: string) => {
  const startRegex = new RegExp(`-----${startWord}-----`, "g");
  const endtRegex = new RegExp(`-----${endWord}-----`, "g");

  return pem.replace(startRegex, "").replace(endtRegex, "").replace(/\s+/g, "");
};

// генерируем ключи
const generateKeyPair = async () => {
  const keypair = forge.pki.rsa.generateKeyPair({ bits: 2048 });

  const privateKeyPem = forge.pki.privateKeyToPem(keypair.privateKey);
  const publicKeyPem = forge.pki.publicKeyToPem(keypair.publicKey);

  return {
    privateKeyPem,
    publicKey: cleanPEM(publicKeyPem, "BEGIN PUBLIC KEY", "END PUBLIC KEY"),
    keypair,
  };
};

// генерируем CSR
const generateCSR = async (keypair: forge.pki.rsa.KeyPair) => {
  // Создание запроса на сертификат (CSR)
  const csr = forge.pki.createCertificationRequest();
  csr.publicKey = keypair.publicKey;
  csr.setSubject([
    { name: "commonName", value: "example.com" },
    { name: "organizationName", value: "Example Organization" },
    { name: "countryName", value: "US" },
  ]);

  // Подписываем CSR приватным ключом
  csr.sign(keypair.privateKey, forge.md.sha256.create());

  // Кодируем CSR в PEM-формат
  const csrPem = forge.pki.certificationRequestToPem(csr);

  return cleanPEM(csrPem, "BEGIN CERTIFICATE REQUEST", "END CERTIFICATE REQUEST");
};

const safePair = async (
  privateKeyPem: string,
  publicKey: string,
  certificateDate: string,
  userId: string,
  companyId: string
) => {
  const privateKeyBase64 = cleanPEM(privateKeyPem, "BEGIN RSA PRIVATE KEY", "END RSA PRIVATE KEY");
  const key = await generateKey(userId);
  const encryptedPrivateKey = await encryptData(privateKeyBase64, key);

  await saveToDB(`${userId}_${companyId}`, {
    privateKeyEncrypted: encryptedPrivateKey,
    publicKey,
    certificateDate: certificateDate,
  });

  return { encryptedPrivateKey };
};

export const findCertificate = async ({
  userId,
  companyId,
}: {
  userId: string | undefined;
  companyId: string | undefined;
}): Promise<{
  encryptedPrivateKey: string;
  privateKey?: any;
}> => {
  if (!userId || !companyId) return Promise.reject("cert_error");
  const savedData = await getFromDB(`${userId}_${companyId}`);
  const savedCertificateDate = savedData?.certificateDate;
  const savedPrivateKey = savedData?.privateKeyEncrypted;

  return new Promise(async (resolve, reject) => {
    if (savedData && savedCertificateDate && savedPrivateKey && new Date(savedCertificateDate) > new Date()) {
      return resolve({ encryptedPrivateKey: savedPrivateKey });
    } else {
      await clearCertDB(userId, companyId);
      const { privateKeyPem, publicKey, keypair } = await generateKeyPair();

      await generateCSR(keypair)
        .then(async (csrKey) => {
          try {
            const response = await fetch(ENDPOINTS.getCertificate(), {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${api.getToken()}`,
              },
              body: csrKey,
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const cert = await response.text();
            const certPem = `-----BEGIN CERTIFICATE-----\n${cert}\n-----END CERTIFICATE-----`;
            const certBase64 = forge.pki.certificateFromPem(certPem);

            const { encryptedPrivateKey } = await safePair(
              privateKeyPem,
              publicKey,
              String(certBase64.validity.notAfter),
              userId,
              companyId
            );

            return resolve({
              encryptedPrivateKey,
              privateKey: keypair.privateKey,
            });
          } catch (error: any) {
            const normalizedError = handleFetchError(error);
            console.error("Certificate fetch error:", normalizedError);
            reject("cert_error");
          }
        })
        .catch(() => reject("cert_error"));
    }
  });
};

const signMessage = (decryptedPrivateKey: any, message: string) => {
  // 1. Декодируем Base64 обратно в бинарный формат (DER)
  const derBytes = forge.util.decode64(decryptedPrivateKey);

  // 2. Преобразуем DER в ASN.1-объект
  const asn1Obj = forge.asn1.fromDer(derBytes);

  // 3. Конвертируем ASN.1 в объект privateKey (тот же формат, что у generateKeyPair)
  const privateKey = forge.pki.privateKeyFromAsn1(asn1Obj);

  // Создаем хэш сообщения (SHA-256)
  const md = forge.md.sha256.create();
  md.update(message, "utf8");

  // Подписываем сообщение
  const signature = privateKey.sign(md);

  // Кодируем подпись в Base64
  return forge.util.encode64(signature);
};

export const signatureMessage = async ({
  userId,
  companyId,
  message,
  user,
  actionModals,
}: {
  userId: string | undefined;
  companyId: string | undefined;
  message: Record<string, any>;
  user: UserStore;
  actionModals: ActionModalsStore;
}): Promise<{ signature: string; jsonMessage: string }> => {
  let numberOfAttempts = 0;

  const signatureMessageHandler = async (): Promise<{
    signature: string;
    jsonMessage: string;
  }> => {
    try {
      const data = await findCertificate({ userId, companyId });

      const { encryptedPrivateKey } = data;
      const encryptionKey = await generateKey(userId!);

      let decryptedPrivateKey;

      try {
        decryptedPrivateKey = await decryptData(encryptedPrivateKey, encryptionKey);
      } catch (e) {
        if (numberOfAttempts < 1) {
          numberOfAttempts++;
          await clearCertDB(userId, companyId);
          return signatureMessageHandler();
        } else {
          throw new Error("Failed to generate signature after multiple attempts");
        }
      }

      const signature = signMessage(decryptedPrivateKey, JSON.stringify(message));

      return {
        signature,
        jsonMessage: JSON.stringify(message),
      };
    } catch (e) {
      actionModals.closeAll();
      user.error = "Сервис временно неисправен, попробуйте позже";
      user.logout();
      throw e;
    }
  };

  return signatureMessageHandler();
};

// Функция для генерации ключа из userId и salt
const generateKey = async (userId: string): Promise<CryptoKey> => {
  const secret = userId + salt;
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey("raw", encoder.encode(secret), { name: "PBKDF2" }, false, [
    "deriveKey",
  ]);

  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: encoder.encode(salt),
      iterations: 100000,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

// Функция для шифрования текста
const encryptData = async (data: string, key: CryptoKey): Promise<string> => {
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96-битный IV для AES-GCM
  const encoder = new TextEncoder();
  const encryptedBuffer = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv: iv }, key, encoder.encode(data));

  const encryptedArray = new Uint8Array(encryptedBuffer);
  const result = new Uint8Array(iv.length + encryptedArray.length);
  result.set(iv);
  result.set(encryptedArray, iv.length);

  return btoa(String.fromCharCode.apply(null, Array.from(result)));
};

// Функция для расшифровки текста
const decryptData = async (encryptedData: string, key: CryptoKey): Promise<string> => {
  const data = atob(encryptedData); // Декодируем из Base64
  const dataArray = new Uint8Array(data.length);
  for (let i = 0; i < data.length; i++) {
    dataArray[i] = data.charCodeAt(i);
  }

  const iv = dataArray.slice(0, 12); // Первые 12 байт — это IV
  const encryptedArray = dataArray.slice(12); // Остальная часть — это зашифрованные данные

  const decoder = new TextDecoder();
  const decryptedBuffer = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv: iv }, key, encryptedArray);

  return decoder.decode(decryptedBuffer);
};

export async function hashMessage(message: string): Promise<string> {
  const messageBytes = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest("SHA-256", messageBytes);
  const hashHex = bytesToHex(Array.from(new Uint8Array(hashBuffer)));
  return hashHex;
}

function bytesToHex(bytes: number[]): string {
  return bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
